<template>
  <div
    :class="[
      usingNewLayout ? 'tw-w-full' : 'tw-w-[421px]',
      'tw-flex tw-flex-col',
    ]"
  >
    <header
      :class="[
        usingNewLayout ? 'tw-h-12' : 'tw-h-[60px] tw-py-3',
        `
          tw-sticky
          tw-top-0
          tw-bg-sf-snow-lighter
          tw-z-10
          tw-flex
          tw-justify-between
          tw-items-center
          tw-px-6
          tw-border-0
          tw-border-b
          tw-border-sf-neutral-lighter-2
          tw-border-solid
        `,
      ]"
    >
      <NewRemarkButton
        v-if="ready && ownerId"
        extra-classes="tw-flex-grow tw-mr-2 tw-text-center"
        :ledger-id="ledgerId"
        :ledger-label="ledgerLabel"
        :owner-type="ownerType"
        :owner-id="ownerId"
        :selected-tag-ids="selectedTagIds"
        modal-size="l"
        :is-client-meeting-remark="true"
      />
      <SfButtonDs
        variant="secondary"
        class="tw-w-9 tw-h-9"
        data-testid="close-talking-points-sidebar-button"
        @click="closeSidebar"
      >
        <i class="fa fa-arrow-right" />
      </SfButtonDs>
    </header>
    <div
      class="tw-py-4 tw-h-full tw-overflow-y-auto"
      data-testid="talking-points-sidebar"
    >
      <template v-if="loading || !ready">
        <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
      </template>
      <template v-else-if="fetchError">
        <p class="tw-text-sf-red-base tw-text-center tw-px-6 tw-py-4">
          {{ I18n.t('detailed_entry.load_error') }}
        </p>
      </template>
      <template v-else>
        <div class="tw-flex tw-flex-col tw-gap-4">
          <div>
            <div
              class="
                tw-flex
                tw-items-center
                tw-justify-between
                tw-border-solid
                tw-border-b
                tw-border-x-0
                tw-border-t-0
                tw-border-sf-snow-dark
                tw-mx-6
              "
            >
              <div class="tw-text-sf-neutral-dark tw-font-medium tw-text-lg">
                {{ I18n.t('detailed_entry.sidebar.meeting_messages') }}
              </div>
            </div>

            <div
              class="
                tw-flex
                tw-flex-col
                tw-gap-1
                tw-rounded-lg
                tw-border-sf-neutral-lighter-2
                tw-bg-sf-snow
                tw-p-2
                tw-mt-4
                tw-mx-6
              "
              data-anchor-id="meeting-remarks"
            >
              <MeetingPresentationTalkingPoints
                v-bind="talkingPointsProps"
                compact
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from 'vuex'
import useI18n from '@app/shared/vue/composables/use-I18n'

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import NewRemarkButton from '@app/shared/vue/components/new-remark/new-remark-button.vue'
import MeetingPresentationTalkingPoints from '@app/application/client_meetings/screens/present/components/talking-points.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'

import { useDrawer } from '@app/shared/pinia/drawer'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import useOrderedRemarks from '@app/application/client_meetings/screens/prepare/composables/use-ordered-remarks'
import usePresentationComments from '@app/application/report/composables/use-presentation-comments'
import { useTalkingPointsSidebarOptions } from '@app/application/client_meetings/store/use-talking-points-sidebar-options'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'

import { SfLog } from '@app/adapters/logger'

import type { Remark } from '@app/application/communication/types'

type RefreshPayload = { action?: string; remark?: Remark }

interface Props {
  extraData?: {
    initial_order: number[]
  }
  ledgerLabel?: string
}
const props = defineProps<Props>()

const usingNewLayout = isUsingNewLayout()
const I18n = useI18n()
const store = useStore()
const presentationMode = window.Silverfin.presentationMode

const drawerStore = useDrawer()
const talkingPointsStore = useTalkingPointsSidebarOptions()
const {
  contextType: storeContextType,
  contextId: storeContextId,
  ledgerId: storeLedgerId,
  clientMeetingId: storeClientMeetingId,
} = storeToRefs(talkingPointsStore)

const ready = computed(
  () => presentationMode || storeContextType.value !== null
)
const contextType = computed(() => storeContextType.value ?? undefined)
const contextId = computed(() => storeContextId.value)
const clientMeetingId = computed(() => storeClientMeetingId.value)
const ledgerId = computed(
  () => storeLedgerId.value ?? store.state.ledgerId ?? 0
)
const ledgerLabel = computed(() => props.ledgerLabel ?? '')
const ownerType = computed(() =>
  presentationMode ? 'ClientMeeting' : contextType.value
)
const ownerId = computed(() =>
  presentationMode ? clientMeetingId.value : contextId.value
)

const { talkingPointTagIds: selectedTagIds } = useTalkingPointTag()

const loading = ref(true)
const fetchError = ref(false)

const { presentationComments } = usePresentationComments()
const { orderedRemarks, orderedItems, joinUnorderedOpposite } =
  useOrderedRemarks()

const talkingPointsProps = computed(() => ({
  items: presentationMode ? orderedItems.value : orderedRemarks.value,
  contextType: presentationMode ? undefined : contextType.value,
  contextId: presentationMode ? undefined : contextId.value,
  ledgerId: ledgerId.value,
  presentationComments: presentationComments.value,
  loading: loading.value,
}))

async function refreshCommunicationData(payload?: RefreshPayload) {
  await store.dispatch('fetchCommunication')

  if (presentationMode && payload) {
    await joinUnorderedOpposite()
  }
}

onMounted(async () => {
  if (!ready.value) {
    SfLog.warn(
      'SidebarTalkingPoints mounted without a contextType in the store. ' +
        'The host page must invoke useTalkingPointsSidebarOptions().setOptions() before opening the drawer.'
    )
  }

  store
    .dispatch('fetchMentionUsernames')
    .catch((err: unknown) =>
      SfLog.warn('fetchMentionUsernames failed', { extra: { err } })
    )

  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)

  try {
    await refreshCommunicationData()
  } catch (error) {
    fetchError.value = true
    SfLog.error(
      'Failed to refresh communication data in talking-points sidebar',
      {
        extra: { error },
      }
    )
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})

function closeSidebar() {
  drawerStore.closeRightDrawerWithoutPersisting(false)
}
</script>
